import React, { useEffect, useState, useMemo } from 'react';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import { OrderMeta, DeleteOrderMutation } from '../../libs/GraphQL';
import {
  Button,
  Row,
  Form,
  FormGroup,
  ControlLabel,
  Col,
  Modal,
} from 'react-bootstrap';
import { contextState } from '../../ContextProvider';
import LoaderButton from '../LoaderButton';
import CategoryConfirmationModal from '../../atoms/CategoryConfirmationModal';
import {
  validateOrdersForReceivedItems,
  normalizeErrorMessage,
  enrichOrderForDisplay,
} from '../../utils/orderValidationUtils';

const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

const STATIC_STYLES = {
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  modalBodyText: {
    marginBottom: '15px',
    fontSize: '16px',
  },
  categoryContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '15px',
  },
  categoryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  categoryRowHover: {
    backgroundColor: '#e9ecef',
    borderColor: '#adb5bd',
  },
  categoryLabel: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#495057',
  },
  categoryCount: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#ffffff',
    backgroundColor: '#6c757d',
    padding: '4px 12px',
    borderRadius: '12px',
  },
  tooltip: {
    position: 'absolute',
    backgroundColor: '#333',
    color: '#fff',
    padding: '12px 15px',
    borderRadius: '6px',
    fontSize: '13px',
    zIndex: 10000,
    maxWidth: '400px',
    maxHeight: '450px',
    overflowY: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
    pointerEvents: 'none',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  tooltipHeader: {
    fontWeight: 'bold',
    marginBottom: '10px',
    borderBottom: '1px solid #555',
    paddingBottom: '6px',
    fontSize: '14px',
  },
  tooltipItem: {
    marginBottom: '12px',
    fontSize: '12px',
    lineHeight: '1.4',
    paddingLeft: '8px',
  },
  tooltipLocation: {
    fontWeight: '500',
    marginBottom: '2px',
    wordBreak: 'break-word',
  },
  tooltipDate: {
    fontSize: '11px',
    color: '#ccc',
    paddingLeft: '12px',
  },
  conflictDisplay: {
    backgroundColor: '#f5f5f5',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginTop: '15px',
    marginBottom: '15px',
    fontFamily: 'monospace',
    fontSize: '14px',
  },
  errorGroup: {
    marginBottom: '20px',
  },
  errorHeader: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '10px',
    fontWeight: 'bold',
    color: '#856404',
  },
  errorDetails: {
    paddingLeft: '10px',
  },
  errorLabel: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
  },
  orderBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  orderBadge: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '12px',
    padding: '4px 8px',
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#495057',
  },
};

const ListOrder = ({ history }) => {
  const [orders, setOrders] = useState([]);
  const [dateRangeFocusedInput, setDateRangeFocusedInput] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null,
  });
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [loadDates, setLoadDates] = useState(false);
  const [checkedOrders, setCheckedOrders] = useState([]);
  const [results, setResults] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryData, setCategoryData] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const { contextData } = contextState();

  const styles = STATIC_STYLES;

  const handleTooltipMouseMove = useMemo(
    () =>
      throttle((e) => {
        setTooltipPosition({ x: e.clientX, y: e.clientY });
      }, 16),
    [setTooltipPosition],
  );

  // Category configuration for rendering - eliminates duplication
  const categoryConfig = [
    { key: 'fridge', label: 'Fridge' },
    { key: 'wholesale', label: 'Wholesale' },
    { key: 'clearout', label: 'Clearout' },
    { key: 'decommission', label: 'Decommission' },
    { key: 'internal', label: 'Internal' },
    { key: 'removal', label: 'Removal' },
    { key: 'other', label: 'Other' },
  ];

  const categorizeOrders = (ordersList) => {
    const categories = {
      fridge: [],
      wholesale: [],
      clearout: [],
      decommission: [],
      internal: [],
      removal: [],
      other: [],
    };

    ordersList.forEach((order) => {
      if (order.orderType === 'FRIDGE' || order.orderType === 'FRIDGE_OTHERS') {
        categories.fridge.push(order);
      } else if (order.orderType === 'CUSTOMER') {
        categories.wholesale.push(order);
      } else if (order.orderType === 'CLEAROUT_DECOMMISSION') {
        categories.decommission.push(order);
      } else if (order.orderType === 'CLEAROUT') {
        categories.clearout.push(order);
      } else if (order.orderType === 'INTERNAL' || order.orderType === 'QA') {
        categories.internal.push(order);
      } else if (order.orderType === 'REMOVAL') {
        categories.removal.push(order);
      } else {
        categories.other.push(order);
      }
    });

    return categories;
  };

  // Helper function to group errors by message type (ignoring unique order IDs)
  const groupErrorsByType = (failedResults, checkedOrders) => {
    const errorGroups = {};

    failedResults.forEach((result, index) => {
      const error = result.reason;
      const fullErrorMessage =
        error.backendMessage || error.message || 'Unknown error';
      const orderId = checkedOrders[index];

      // Extract base error message using utility function
      const baseMessage = normalizeErrorMessage(fullErrorMessage);

      // Use base message as grouping key, but store full message for display
      const groupKey = baseMessage;

      if (!errorGroups[groupKey]) {
        errorGroups[groupKey] = {
          baseMessage: baseMessage,
          fullMessage: fullErrorMessage,
          orders: [],
        };
      }

      // Find the order details for additional context
      const order = orders.find((o) => o.uniqueId === orderId);
      const enrichedOrder = enrichOrderForDisplay(order);
      const locationId = enrichedOrder.locationId;
      const deliveryDate = enrichedOrder.deliveryDate;

      errorGroups[groupKey].orders.push({
        orderId: orderId,
        orderIndex: index + 1,
        locationId: locationId,
        deliveryDate: deliveryDate,
      });
    });

    return Object.values(errorGroups);
  };

  // Helper functions for modal management
  const createModalHandlers = (setShowModal, setData, onHideCallback) => ({
    show: (data) => {
      setData(data);
      setShowModal(true);
    },
    hide: () => {
      if (!isDeleting) {
        setShowModal(false);
        setData(null);
        // Trigger callback if provided (e.g., page reload)
        if (onHideCallback) {
          onHideCallback();
        }
      }
    },
    isVisible: (showModal) => showModal && !isDeleting,
  });

  // Reusable loading overlay component
  const LoadingOverlay = ({ isLoading, text }) => {
    if (!isLoading) return null;

    return (
      <div style={styles.loadingOverlay}>
        <LoaderButton
          block
          bsStyle="primary"
          bsSize="large"
          disabled
          isLoading={true}
          text=""
          loadingText={text}
        />
      </div>
    );
  };

  // Reusable modal footer component
  const ModalFooter = ({
    onCancel,
    onConfirm,
    cancelText = 'Cancel',
    confirmText,
    confirmStyle = 'danger',
  }) => (
    <Modal.Footer>
      <Button onClick={onCancel} disabled={isDeleting}>
        {cancelText}
      </Button>
      {onConfirm && (
        <Button
          bsStyle={confirmStyle}
          onClick={onConfirm}
          disabled={isDeleting}
        >
          {confirmText}
        </Button>
      )}
    </Modal.Footer>
  );

  // Modal handlers using the helper function
  const categoryModalHandlers = createModalHandlers(
    setShowCategoryModal,
    setCategoryData,
  );
  const warningModalHandlers = createModalHandlers(
    setShowWarningModal,
    setConflictData,
  );
  const errorModalHandlers = createModalHandlers(
    setShowErrorModal,
    setErrorData,
    () => {
      // Reset warning modal state and reload page when error modal is closed
      setShowWarningModal(false);
      setConflictData(null);
      history.go(0);
      history.push('/orders/list');
    },
  );

  useEffect(() => {
    // Checking if the date is being changed
    let days = enumerateDaysBetweenDates(
      dateRange.startDate,
      dateRange.endDate,
    );
    // if a user only selects one day
    if (
      moment(dateRange.startDate).format('YYYY-MM-DD') ===
      moment(dateRange.endDate).format('YYYY-MM-DD')
    ) {
      days = [moment(dateRange.startDate).startOf('day').toDate()];
    }
    if (loadDates && days.length) {
      getRangeOfData(days);
    }
  }, [checkedOrders, dateRange, loadDates]);

  const handleSelect = (row, isSelect) => {
    if (isSelect) {
      setCheckedOrders([...checkedOrders, row.uniqueId]);
    } else {
      const updatedList = checkedOrders;
      const itemIndex = updatedList.indexOf(row.uniqueId);
      if (itemIndex > -1) {
        updatedList.splice(itemIndex, 1);
      }
      setCheckedOrders([...updatedList]);
    }
  };

  const handleSelectAll = (isSelectAll, rows) => {
    if (isSelectAll) {
      const updatedList = rows.map((order) => order.uniqueId);
      setCheckedOrders(updatedList);
    } else {
      setCheckedOrders([]);
    }
  };

  const renderUniqueLocationId = (cell, row) => {
    return (
      <Link target="_blank" to={'/orders/order_id/' + row.uniqueId}>
        {cell}
      </Link>
    );
  };

  const enumerateDaysBetweenDates = function (startDate, endDate) {
    let dates = [];

    const currDate = moment(startDate).startOf('day');
    const lastDate = moment(endDate).startOf('day');

    dates.push(moment(dateRange.startDate).startOf('day').toDate());
    while (currDate.add(1, 'days').diff(lastDate) < 0) {
      dates.push(currDate.clone().toDate());
    }
    dates.push(moment(dateRange.endDate).startOf('day').toDate());
    return dates;
  };

  const getRangeOfData = async (days) => {
    const daysCount = Object.keys(days).length;
    setIsOrdersLoading(true);
    let orderValues = [];
    const orderPromises = [];
    for (const day of days) {
      orderPromises.push(
        OrderMeta(null, null, moment(day).format('YYYY-MM-DD')),
      );
    }
    const fetchOrder = (nextToken, date) => {
      OrderMeta(nextToken, null, date).then((res) => {
        setOrders((orders) => [...orders, ...res.data.ordersByDay.orders]);
        setResults((orders) => [...orders, ...res.data.ordersByDay.orders]);
        if (res.data.ordersByDay.nextToken !== null) {
          fetchOrder(
            res.data.ordersByDay.nextToken,
            res.data.ordersByDay.orders[0].deliveryDate,
          );
        }
      });
    };

    Promise.all(orderPromises).then((values) => {
      values.forEach((val) => {
        if (val.data.ordersByDay.nextToken !== null) {
          fetchOrder(
            val.data.ordersByDay.nextToken,
            val.data.ordersByDay.orders[0].deliveryDate,
          );
        }
        orderValues.push(...val.data.ordersByDay.orders);
      });
      setIsOrdersLoading(false);
      setLoadDates(false);
      setOrders((orders) => [...orders, ...orderValues]);
      setResults((orders) => [...orders, ...orderValues]);
    });
  };

  // Function to handle order deletion
  const handleDeleteOrders = async (ordersWithReceivedItems = null) => {
    setIsDeleting(true);

    try {
      const deletionPromises = [];

      for (const orderId of checkedOrders) {
        // Find the order details to get locationId and dropOffDate for proper key structure
        const order = orders.find((o) => o.uniqueId === orderId);

        if (!order) {
          console.error(`Order ${orderId} not found in orders list`);
          continue;
        }

        // Find if this order has received items
        const orderConflict = ordersWithReceivedItems
          ? ordersWithReceivedItems.find(
              (conflict) => conflict.orderId === orderId,
            )
          : null;

        if (orderConflict) {
          // Delete with received items metadata
          deletionPromises.push(
            DeleteOrderMutation(
              orderId,
              orderConflict.locationId,
              orderConflict.dropOffDate,
              orderConflict.receivedItems,
            ),
          );
        } else {
          // Delete without metadata
          deletionPromises.push(DeleteOrderMutation(orderId));
        }
      }

      // Use Promise.allSettled to get results for both successful and failed deletions
      const results = await Promise.allSettled(deletionPromises);

      const successful = results.filter(
        (result) => result.status === 'fulfilled',
      ).length;
      const failed = results.filter(
        (result) => result.status === 'rejected',
      ).length;

      // Show appropriate success/error messages based on results
      if (failed === 0) {
        // All deletions successful
        if (ordersWithReceivedItems) {
          toast.success(
            'Orders deleted successfully. Demand Planning has been notified.',
          );
        } else {
          toast.success('Orders deleted successfully');
        }
      } else if (successful > 0) {
        // Partial success - some orders deleted, some failed
        const failedResults = results.filter(
          (result) => result.status === 'rejected',
        );

        // Show success message with callback for navigation
        if (ordersWithReceivedItems) {
          toast.success(
            'Orders deleted successfully. Demand Planning has been notified.',
            {
              onClose: () => {
                setCheckedOrders([]);
                setIsDeleting(false);
                setShowWarningModal(false);
                history.go(0);
                history.push(`/orders/list`);
              },
            },
          );
        }

        if (failedResults.length > 0) {
          const groupedErrors = groupErrorsByType(failedResults, checkedOrders);

          setErrorData({
            type: 'partial',
            successful: successful,
            total: checkedOrders.length,
            failed: failed,
            errors: groupedErrors,
          });
          setShowErrorModal(true);
        }
      } else {
        // All deletions failed
        const failedResults = results.filter(
          (result) => result.status === 'rejected',
        );

        if (failedResults.length > 0) {
          const groupedErrors = groupErrorsByType(failedResults, checkedOrders);

          setErrorData({
            type: 'complete',
            total: checkedOrders.length,
            errors: groupedErrors,
          });
          setShowErrorModal(true);
        }
      }

      // Reset and redirect only if all operations were successful (no errors)
      if (failed === 0 && successful > 0) {
        setCheckedOrders([]);
        setShowWarningModal(false);
        setShowErrorModal(false);
        setErrorData(null);

        // Add small delay to ensure any final state updates are visible before page refresh
        setTimeout(() => {
          history.go(0);
          history.push(`/orders/list`);
        }, 1000);
      }

      setIsDeleting(false);
    } catch (error) {
      // Fallback error handling for unexpected errors
      toast.error(
        `An unexpected error occurred: ${error.message}. Please retry the operation.`,
      );

      console.error(error);
    }
  };

  const proceedWithValidation = async () => {
    setShowCategoryModal(false);
    setIsValidating(true);

    try {
      // Get full order details for selected orders
      const selectedOrders = orders.filter((order) =>
        checkedOrders.includes(order.uniqueId),
      );

      const conflicts = await validateOrdersForReceivedItems(selectedOrders);

      setIsValidating(false);

      if (conflicts) {
        // Show warning modal
        setConflictData(conflicts);
        setShowWarningModal(true);
      } else {
        // No conflicts, proceed with deletion
        await handleDeleteOrders();
      }
    } catch (error) {
      setIsValidating(false);
      console.error('Validation error:', error);
      window.alert('Something went wrong. Please reload and try again.');
    }
  };

  const initiateDeleteOrders = () => {
    const selectedOrders = orders.filter((order) =>
      checkedOrders.includes(order.uniqueId),
    );

    const categories = categorizeOrders(selectedOrders);

    const categoryBreakdown = {
      fridge: {
        count: categories.fridge.length,
        orders: categories.fridge,
      },
      wholesale: {
        count: categories.wholesale.length,
        orders: categories.wholesale,
      },
      clearout: {
        count: categories.clearout.length,
        orders: categories.clearout,
      },
      decommission: {
        count: categories.decommission.length,
        orders: categories.decommission,
      },
      internal: {
        count: categories.internal.length,
        orders: categories.internal,
      },
      removal: {
        count: categories.removal.length,
        orders: categories.removal,
      },
      other: {
        count: categories.other.length,
        orders: categories.other,
      },
      total: selectedOrders.length,
    };

    setCategoryData(categoryBreakdown);
    setShowCategoryModal(true);
  };

  return (
    <div>
      <Row style={{ marginLeft: '0px' }}>
        <Col>
          <br />
          <Form inline>
            <FormGroup controlId="searchBar">
              <ControlLabel>CMS Delivery Date Selection</ControlLabel> <br />
              <div data-testid="searchDate">
                <DateRangePicker
                  startDate={dateRange.startDate}
                  startDateId="dateRangeStart"
                  endDate={dateRange.endDate}
                  endDateId="dateRangeEnd"
                  isOutsideRange={() => false}
                  onDatesChange={({ startDate, endDate }) => {
                    setDateRange({ startDate, endDate });
                  }}
                  minimumNights={0}
                  focusedInput={dateRangeFocusedInput}
                  onFocusChange={(focusedInput) => {
                    setDateRangeFocusedInput(focusedInput);
                  }}
                />
                <button
                  disabled={isOrdersLoading}
                  data-testid="btnChangeDate"
                  onClick={(e) => {
                    e.preventDefault();
                    setLoadDates(true);
                    setCheckedOrders([]);
                    setOrders([]);
                    setResults([]);
                  }}
                  style={{ marginLeft: '10px' }}
                >
                  Load
                </button>
              </div>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      {isOrdersLoading && <div>Loading</div>}
      {orders && orders.length === 0 && !isOrdersLoading && (
        <div>No Results</div>
      )}
      {orders && orders.length > 0 && !isOrdersLoading && (
        <div style={{ borderLeft: '0px' }}>
          <div style={{ display: 'flex' }}>
            {checkedOrders.length > 0 &&
              contextData.features.canDeleteOrders === true && (
                <div>
                  <ControlLabel>Actions</ControlLabel>
                  <br />
                  <Button
                    bsStyle="warning"
                    data-testid="btnDeleteOrders"
                    onClick={initiateDeleteOrders}
                    disabled={isValidating || isDeleting}
                  >
                    Delete Selected Orders
                  </Button>
                </div>
              )}
          </div>
          <br />
          <span style={{ fontWeight: 'bold' }}>
            Order ({checkedOrders.length} selected out of {results.length})
          </span>
          <Row
            style={{
              maxHeight: 'calc(100vh - 350px)',
              overflowX: 'auto',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              margin: 'auto',
            }}
          >
            <Col>
              <BootstrapTable
                data-testid="orderListTable"
                keyField="uniqueId"
                classes="orders-list-table"
                data={results.map((order) => {
                  let uniqueLocationId = order.deliveryDate + '-';
                  if (order.wholesaleLocationId) {
                    uniqueLocationId += order.wholesaleLocationId;
                  } else if (order.locationId) {
                    uniqueLocationId += order.locationId;
                  } else if (order.addressForDelivery) {
                    uniqueLocationId += order.addressForDelivery;
                  } else {
                    uniqueLocationId += order.orderType;
                  }
                  let newOrder = {
                    uniqueId: order.uniqueId,
                    owner: order.orderOwner || 'internal',
                    dropOffDate: order.dropOffDate,
                    uniqueLocationId: uniqueLocationId,
                    deliverySpecialNotes: order.deliverySpecialNotes,
                    orderUploadId: order.orderUploadId,
                  };
                  return newOrder;
                })}
                columns={[
                  {
                    text: 'Order',
                    filter: textFilter(),
                    dataField: 'uniqueLocationId',
                    formatter: renderUniqueLocationId,
                  },
                  {
                    text: 'Upload Id',
                    filter: textFilter(),
                    dataField: 'orderUploadId',
                  },
                  {
                    text: 'Owner',
                    filter: textFilter(),
                    dataField: 'owner',
                  },
                  {
                    text: 'Drop Off Date',
                    filter: textFilter(),
                    dataField: 'dropOffDate',
                  },
                  {
                    text: 'Delivery Special Notes',
                    filter: textFilter(),
                    dataField: 'deliverySpecialNotes',
                  },
                ]}
                filter={filterFactory()}
                selectRow={{
                  mode: 'checkbox',
                  onSelect: handleSelect,
                  onSelectAll: handleSelectAll,
                  selected: checkedOrders,
                }}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* Loading States */}
      <LoadingOverlay
        isLoading={isValidating}
        text="Checking for received items..."
      />
      <LoadingOverlay
        isLoading={isDeleting && !showWarningModal}
        text="Deleting orders..."
      />

      {/* Category Confirmation Modal */}
      <CategoryConfirmationModal
        show={showCategoryModal}
        onHide={categoryModalHandlers.hide}
        onConfirm={proceedWithValidation}
        categoryData={categoryData}
        categoryConfig={categoryConfig}
        hoveredCategory={hoveredCategory}
        setHoveredCategory={setHoveredCategory}
        tooltipPosition={tooltipPosition}
        setTooltipPosition={setTooltipPosition}
        handleTooltipMouseMove={handleTooltipMouseMove}
        title={`Confirm Deletion of ${categoryData?.total || 0} Order${
          categoryData?.total !== 1 ? 's' : ''
        }`}
        message="You are about to delete the following orders (hover to see details):"
        confirmText="Confirm Deletion"
        renderTooltipContent={(hoveredCategory, categoryData) => (
          <>
            <div style={styles.tooltipHeader}>
              {hoveredCategory.charAt(0).toUpperCase() +
                hoveredCategory.slice(1)}{' '}
              Orders ({categoryData[hoveredCategory].count})
            </div>
            {categoryData[hoveredCategory].orders
              .slice(0, 5)
              .map((order, idx) => {
                const locationDisplay = order.wholesaleLocationId
                  ? order.wholesaleLocationId
                  : order.locationId || order.addressForDelivery || 'N/A';
                const dateLabel = order.dropOffDate
                  ? 'Drop Off'
                  : order.deliveryDate
                  ? 'Delivery'
                  : 'Date';
                const dateValue =
                  order.dropOffDate || order.deliveryDate || 'N/A';
                return (
                  <div key={order.uniqueId || idx} style={styles.tooltipItem}>
                    <div style={styles.tooltipLocation}>
                      • {locationDisplay}
                    </div>
                    <div style={styles.tooltipDate}>
                      {dateLabel}: {dateValue}
                    </div>
                  </div>
                );
              })}
            {categoryData[hoveredCategory].count > 5 && (
              <div style={{ ...styles.tooltipItem, fontStyle: 'italic' }}>
                ... and {categoryData[hoveredCategory].count - 5} more
              </div>
            )}
          </>
        )}
        styles={styles}
      />

      {/* Warning Modal for Received Items Conflicts */}
      <Modal show={showWarningModal} onHide={warningModalHandlers.hide}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Warning: Items Already Have Received Counts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            For the following orders, one or more items already have received
            counts. Demand Planning will be alerted about this cancellation.
          </p>
          <div style={styles.conflictDisplay}>
            {conflictData &&
              Array.isArray(conflictData) &&
              conflictData.map((conflict, index) => (
                <div key={index} style={{ marginBottom: '10px' }}>
                  <div>
                    <strong>Order #:</strong> {index + 1},{' '}
                    <strong>Drop Off Date:</strong> {conflict.dropOffDate}
                  </div>
                  <div>
                    <strong>Location:</strong> {conflict.locationId}
                  </div>
                </div>
              ))}
          </div>
        </Modal.Body>
        <ModalFooter
          onCancel={warningModalHandlers.hide}
          onConfirm={() => handleDeleteOrders(conflictData)}
          confirmText={isDeleting ? 'Deleting...' : 'Confirm'}
        />
      </Modal>

      {/* Error Modal for Deletion Failures */}
      <Modal show={showErrorModal} onHide={errorModalHandlers.hide}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>
            {errorData?.type === 'partial'
              ? 'Partial Deletion Success'
              : 'Deletion Failed'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!errorData ? (
            <p style={styles.modalBodyText}>Loading error details...</p>
          ) : errorData?.type === 'partial' ? (
            <p style={styles.modalBodyText}>
              <strong>
                {errorData.successful}/{errorData.total}
              </strong>{' '}
              orders were deleted successfully.
              <strong> {errorData.failed} orders</strong> could not be deleted
              due to the following errors:
            </p>
          ) : (
            <p style={styles.modalBodyText}>
              Failed to delete <strong>{errorData.total} orders</strong> due to
              the following errors:
            </p>
          )}

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {errorData?.errors && errorData.errors.length > 0 ? (
              errorData.errors.map((errorGroup, groupIndex) => {
                const orderCount = errorGroup.orders.length;
                const showCompressedView = orderCount > 3;

                return (
                  <div key={groupIndex} style={styles.errorGroup}>
                    <div style={styles.errorHeader}>
                      {errorGroup.baseMessage}
                    </div>
                    <div style={styles.errorDetails}>
                      <div style={styles.errorLabel}>
                        {showCompressedView
                          ? `Affected Orders (${orderCount} total):`
                          : 'Affected Orders:'}
                      </div>
                      <div style={styles.orderBadges}>
                        {showCompressedView ? (
                          <>
                            {errorGroup.orders
                              .slice(0, 3)
                              .map((order, orderIndex) => (
                                <span
                                  key={orderIndex}
                                  style={styles.orderBadge}
                                >
                                  {order.locationId} - {order.deliveryDate}
                                </span>
                              ))}
                            <span style={styles.orderBadge}>
                              +{orderCount - 3} more
                            </span>
                          </>
                        ) : (
                          errorGroup.orders.map((order, orderIndex) => (
                            <span key={orderIndex} style={styles.orderBadge}>
                              {order.locationId} - {order.deliveryDate}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p style={styles.modalBodyText}>No error details available.</p>
            )}
          </div>
        </Modal.Body>
        <ModalFooter onCancel={errorModalHandlers.hide} cancelText="Close" />
      </Modal>
    </div>
  );
};

export default ListOrder;
